'use client';

import React from 'react';
import { LucideIcon, type LucideIconName } from '@skin-seoul/design-system';
import {
  DeliveryTrackingStatus,
  ParxlTrackingStatus,
  YunExpressTrackingStatus,
  type TrackingEvent,
} from '@skin-seoul/service';

interface TrackingTimelineProps {
  events: TrackingEvent[];
}

const DEFAULT_ICON_NAME: LucideIconName = 'CircleCheck';

const DELEO_STATUS_ICONS: Record<DeliveryTrackingStatus, LucideIconName> = {
  [DeliveryTrackingStatus.Delivered]: 'CircleCheck',
  [DeliveryTrackingStatus.CustomsCleared]: 'CircleCheck',
  [DeliveryTrackingStatus.InCustoms]: 'Circle',
  [DeliveryTrackingStatus.ArrivedDestination]: 'Globe',
  [DeliveryTrackingStatus.Departed]: 'Plane',
  [DeliveryTrackingStatus.InvoiceIssued]: 'Box',
};

const PARXL_STATUS_ICONS: Record<ParxlTrackingStatus, LucideIconName> = {
  [ParxlTrackingStatus.Delivered]: 'CircleCheck',
  [ParxlTrackingStatus.CustomCleared]: 'CircleCheck',
  [ParxlTrackingStatus.Booked]: 'Circle',
  [ParxlTrackingStatus.BookingReceived]: 'Circle',
  [ParxlTrackingStatus.AwaitingCollection]: 'Circle',
  [ParxlTrackingStatus.PickedUp]: 'Box',
  [ParxlTrackingStatus.ReceivedAtExportHub]: 'Box',
  [ParxlTrackingStatus.ProcessingAtExportHub]: 'Box',
  [ParxlTrackingStatus.ReadyToTransfer]: 'Plane',
  [ParxlTrackingStatus.ReceivedAtAirlineTerminal]: 'Plane',
  [ParxlTrackingStatus.DepartedFromOrigin]: 'Plane',
  [ParxlTrackingStatus.InTransit]: 'Plane',
  [ParxlTrackingStatus.ArrivedAtDestinationAirport]: 'Globe',
  [ParxlTrackingStatus.ArrivedAtCustomsFacility]: 'Globe',
  [ParxlTrackingStatus.EnrouteToSortingHub]: 'Globe',
  [ParxlTrackingStatus.ArrivedAtSortingHub]: 'Globe',
  [ParxlTrackingStatus.ArrivedAndProcessingAtSortingHub]: 'Globe',
  [ParxlTrackingStatus.HandedOverForCustoms]: 'PackageSearch',
  [ParxlTrackingStatus.OutForDelivery]: 'Truck',
  [ParxlTrackingStatus.Cancelled]: 'CircleAlert',
  [ParxlTrackingStatus.Expired]: 'CircleAlert',
  [ParxlTrackingStatus.BookingRejected]: 'CircleAlert',
  [ParxlTrackingStatus.UnsuccessfulDelivery]: 'CircleAlert',
  [ParxlTrackingStatus.FinalUnsuccessfulDelivery]: 'CircleAlert',
  [ParxlTrackingStatus.Lost]: 'CircleAlert',
  [ParxlTrackingStatus.Disposed]: 'CircleAlert',
  [ParxlTrackingStatus.DamagedAtDestination]: 'CircleAlert',
  [ParxlTrackingStatus.Damaged]: 'CircleAlert',
  [ParxlTrackingStatus.PossibleDelay]: 'CircleAlert',
  [ParxlTrackingStatus.Relabelled]: 'RefreshCwIcon',
  [ParxlTrackingStatus.Repackaged]: 'RefreshCwIcon',
  [ParxlTrackingStatus.Translated]: 'RefreshCwIcon',
  [ParxlTrackingStatus.AddressChange]: 'RefreshCwIcon',
};

const YUN_EXPRESS_STATUS_ICONS: Record<string, LucideIconName> = {
  [YunExpressTrackingStatus.Delivered]: 'CircleCheck',
  [YunExpressTrackingStatus.Returned]: 'CircleAlert',
  [YunExpressTrackingStatus.ReturnedBack]: 'CircleAlert',
  [YunExpressTrackingStatus.ReturnedToSender]: 'CircleAlert',
  [YunExpressTrackingStatus.OrderCreation]: 'Circle',
  [YunExpressTrackingStatus.PreInfo]: 'Circle',
  [YunExpressTrackingStatus.PreAdvicing]: 'Circle',
  [YunExpressTrackingStatus.Edd]: 'Circle',
  [YunExpressTrackingStatus.PickedUp]: 'Box',
  [YunExpressTrackingStatus.FirstMileArrive]: 'Box',
  [YunExpressTrackingStatus.FirstMileDepart]: 'Box',
  [YunExpressTrackingStatus.DepartConfirmOC]: 'Plane',
  [YunExpressTrackingStatus.ArriveConfirmOC]: 'Plane',
  [YunExpressTrackingStatus.MainLineDepart]: 'Plane',
  [YunExpressTrackingStatus.MainLineArrive]: 'Globe',
  [YunExpressTrackingStatus.InTransit]: 'Plane',
  [YunExpressTrackingStatus.TransitIn]: 'Plane',
  [YunExpressTrackingStatus.TransitOut]: 'Plane',
  [YunExpressTrackingStatus.TransithubArrive]: 'Globe',
  [YunExpressTrackingStatus.ChangeWaybillInfo]: 'RefreshCwIcon',
  [YunExpressTrackingStatus.PickupCargoTerminal]: 'Box',
  [YunExpressTrackingStatus.ArriveOrinCustoms]: 'PackageSearch',
  [YunExpressTrackingStatus.CustomsProcessing]: 'PackageSearch',
  [YunExpressTrackingStatus.CustomsComplete]: 'CircleCheck',
  [YunExpressTrackingStatus.CustomsRelease]: 'CircleCheck',
  [YunExpressTrackingStatus.ExportCustomsComplete]: 'CircleCheck',
  [YunExpressTrackingStatus.AirportRelease]: 'CircleCheck',
  [YunExpressTrackingStatus.CarrierPickup]: 'Truck',
  [YunExpressTrackingStatus.InTransitCarrier]: 'Truck',
  [YunExpressTrackingStatus.DeliveryAttempt]: 'Truck',
  [YunExpressTrackingStatus.AirportHold]: 'CircleAlert',
  [YunExpressTrackingStatus.AirportInspection]: 'CircleAlert',
  [YunExpressTrackingStatus.CustomsDelay]: 'CircleAlert',
  [YunExpressTrackingStatus.CustomsHold]: 'CircleAlert',
  [YunExpressTrackingStatus.CustomsInspection]: 'CircleAlert',
  [YunExpressTrackingStatus.DeliveryFailure]: 'CircleAlert',
  [YunExpressTrackingStatus.PackageException]: 'CircleAlert',
  [YunExpressTrackingStatus.PackageLost]: 'CircleAlert',
  [YunExpressTrackingStatus.Other]: 'CircleAlert',
};

const getIconForEvent = (eventType: string): LucideIconName => {
  const yunExpressIcon = YUN_EXPRESS_STATUS_ICONS[eventType as YunExpressTrackingStatus];
  if (yunExpressIcon) return yunExpressIcon;

  if (eventType in ParxlTrackingStatus) {
    return PARXL_STATUS_ICONS[eventType as ParxlTrackingStatus] ?? DEFAULT_ICON_NAME;
  }

  const parxlIcon = PARXL_STATUS_ICONS[eventType as ParxlTrackingStatus];
  if (parxlIcon) return parxlIcon;

  return DELEO_STATUS_ICONS[eventType as DeliveryTrackingStatus] ?? DEFAULT_ICON_NAME;
};

export const TrackingTimeline = ({ events }: TrackingTimelineProps) => {
  return (
    <div className="flex flex-col gap-4 w-full items-start">
      {events.map((event) => {
        const iconName = getIconForEvent(event.type as string);
        const iconColor = event.status === 'completed'
          ? 'var(--color-success-600, #16a34a)'
          : 'var(--color-neutral-700, #44404b)';

        return (
          <React.Fragment key={event.id}>
            <div className="flex flex-row gap-4 w-full items-start">
              <div
                className="w-5 h-5 rounded-full shrink-0"
                style={{ color: iconColor }}
              >
                <LucideIcon name={iconName} size={20} />
              </div>

              <div className="flex flex-col gap-1 flex-1 items-start">
                <p className="text-label-small font-medium text-neutral-700">{event.title}</p>
                <p className="text-label-small font-normal text-neutral-400">{event.timestamp}</p>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
